import React, { useCallback, useMemo, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useNavigate } from 'react-router-dom';
import {
  OnChangeFn,
  Row,
  SortingState,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { LiaSortDownSolid, LiaSortSolid, LiaSortUpSolid } from 'react-icons/lia';
import { useSelector } from 'react-redux';

import { TableInfo } from '@/services/table/tableTypes';
import { replacePathParams } from '@/common/utils/utils';
import ROUTES from '@/routers/Routes';
import useIsMobileView from '@/hooks/useIsMobileView';
import useGetTables from '@/services/lobby/queries/useGetTables';
import TableInfoModal from '@/pages/table/components/TableInfoModal/TableInfoModal';
import TableStakeInfo from '@/components/TableStakeInfo/TableStakeInfo';
import { ReducerState } from '@/services/common/lib/reducerState';

import LobbyStatusLoader from './LobbyStatusLoader';
import TableBuyInInfo from './TableBuyInInfo';
import TablePotInfo from './TablePotInfo';
import TableStatus from '@/components/TableStatus/TableStatus';

const columnHelper = createColumnHelper<TableInfo>();

const LobbyTableDesktop = () => {
  const filters = useSelector((state: ReducerState) => state.auth?.lobbyFilters);

  const navigate = useNavigate();
  const isMobile = useIsMobileView();

  const [selectedRow, setSelectedRow] = useState<TableInfo | null>(null);
  const [showTableInfo, setShowTableInfo] = useState(false);

  const selectTable = useCallback(
    async (tableInfo: TableInfo) => {
      if (!tableInfo.id) return;

      const url = replacePathParams(ROUTES.TABLE, {
        tableId: tableInfo.id,
      });

      if (isMobile) {
        return navigate(url, { replace: true });
      }

      const width = 1280;
      const height = 780;
      const newWindow = window.open(url, tableInfo.id, `height=${height},width=${width}`);
      if (newWindow) {
        newWindow.onload = () => {
          newWindow.focus();
        };
      }
    },
    [isMobile, navigate],
  );

  const tableContainerRef = React.useRef<HTMLDivElement>(null);
  const [sorting, setSorting] = React.useState<SortingState>([
    {
      desc: false,
      id: 'maxStake',
    },
  ]);

  const { data, isFetching, fetchNextPage, isRefetching } = useGetTables({
    filter: filters,
    ...(sorting.length > 0 && {
      sort: {
        orderBy: sorting[0]?.id as keyof TableInfo | 'seats',
        sortOrder: sorting[0]?.desc ? 'desc' : 'asc',
      },
    }),
    paginationOptions: { skip: 0, take: 20 },
  });

  const createTableClickHandler = useMemo(
    () => (row: Row<TableInfo>) => () => selectTable(row.original),
    [selectTable],
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        enableSorting: true,
        header: 'Table Name',
        minSize: 500,
        cell: ({ row }) => (
          <TableStatus
            playerCount={row.original.players}
            activePlayerCount={row.original.seats.reduce((acc, seat) => acc + (seat?.active ? 1 : 0), 0)}
            maxPlayers={row.original.maxPlayers}
            tableName={row.original.name}
            tableGame={row.original.game}
            onClick={createTableClickHandler(row)}
            variantsCount={row.original.variants.length}
            onInfoIconClick={() => {
              setShowTableInfo(true);
              setSelectedRow(row.original);
            }}
            padding={false}
          />
        ),
      }),
      columnHelper.accessor('maxStake', {
        enableSorting: true,
        header: 'Stakes',
        minSize: 420,
        cell: ({ row }) => (
          <div className="flex flex-1 items-center justify-end text-white">
            <TableStakeInfo
              ante={row.original.ante}
              smallBet={row.original.smallBet}
              bigBet={row.original.bigBet}
              smallBlind={row.original.smallBlind}
              bigBlind={row.original.bigBlind}
              fontSize="text-sm"
            />
          </div>
        ),
      }),
      columnHelper.accessor('maxBuyin', {
        enableSorting: true,
        header: 'Buy-In',
        minSize: 200,
        cell: ({ row }) => (
          <div className="flex flex-1 items-center justify-end text-white">
            <TableBuyInInfo minBuyin={row.original.minBuyin} maxBuyin={row.original.maxBuyin} fontSize="text-sm" />
          </div>
        ),
      }),
      columnHelper.accessor('avgPot', {
        enableSorting: false,
        header: 'Avg. Pot',
        cell: ({ getValue }) => (
          <div className="flex flex-1 items-center justify-end text-white">
            <TablePotInfo averagePot={getValue()} fontSize="text-sm" />
          </div>
        ),
      }),
      columnHelper.accessor('maxPlayers', {
        id: 'seats',
        enableSorting: true,
        header: 'Players',
        cell: ({ row }) => (
          <div className="flex flex-1 items-center justify-end text-white">
            <span className="flex items-center text-sm">
              {row.original.players}/{row.original.maxPlayers}
            </span>
          </div>
        ),
      }),
      columnHelper.accessor('waitlist', {
        id: 'waitlist',
        enableSorting: true,
        header: 'Waitlist',
        cell: ({ row }) => (
          <div className="flex flex-1 items-center justify-end text-white">
            <span className="ml-2 flex items-center text-sm">{row.original.waitlist.length}</span>
          </div>
        ),
      }),
    ],
    [],
  );

  const flatData = React.useMemo(() => data?.pages?.flatMap((page) => page.data.tables) ?? [], [data]);
  const totalDBRowCount = data?.pages[0].data.total ?? 0;
  const totalFetched = flatData.length;

  const fetchMoreOnBottomReached = React.useCallback(
    (containerRefElement?: HTMLDivElement | null) => {
      if (containerRefElement) {
        const { scrollHeight, scrollTop, clientHeight } = containerRefElement;

        if (scrollHeight - scrollTop - clientHeight < 500 && !isFetching && totalFetched < totalDBRowCount) {
          fetchNextPage();
        }
      }
    },
    [fetchNextPage, isFetching, totalFetched, totalDBRowCount],
  );

  React.useEffect(() => {
    fetchMoreOnBottomReached(tableContainerRef.current);
  }, [fetchMoreOnBottomReached]);

  const handleSortingChange: OnChangeFn<SortingState> = (updater) => {
    setSorting(updater);

    if (tableInstance.getRowModel().rows.length) rowVirtualizer.scrollToIndex?.(0);
  };

  const tableInstance = useReactTable<TableInfo>({
    columns,
    data: flatData,
    state: {
      sorting,
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualSorting: true,
    defaultColumn: {
      maxSize: Number.MAX_SAFE_INTEGER,
    },
  });

  const { rows } = tableInstance.getRowModel();

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    estimateSize: () => 95,
    getScrollElement: () => tableContainerRef.current,
    measureElement:
      typeof window !== 'undefined' && navigator.userAgent.indexOf('Firefox') === -1
        ? (element) => element?.getBoundingClientRect().height
        : undefined,
    overscan: 5,
  });

  tableInstance.setOptions((prev) => ({
    ...prev,
    onSortingChange: handleSortingChange,
  }));

  return (
    <div
      className="scrollbar-phenom relative max-h-[84vh] w-full overflow-auto"
      ref={tableContainerRef}
      onScroll={(e) => fetchMoreOnBottomReached(e.target as HTMLDivElement)}
    >
      <table className="w-full table-fixed">
        <thead className="sticky top-0 z-10">
          {tableInstance.getHeaderGroups().map((headerGroup) => (
            <tr
              className="relative flex h-10 items-center !rounded-[0.625em] text-left backdrop-blur border-glow"
              key={headerGroup.id}
            >
              {headerGroup.headers.map((header, index) => {
                const canHeaderSort = header.column.getCanSort();
                return (
                  <th
                    key={header.id}
                    className={twMerge(
                      canHeaderSort ? 'hover:cursor-pointer hover:text-white' : 'cursor-default',
                      // bg glossy white
                      'box-border',
                      'flex items-center px-3 font-bold text-ph-white-30 first:rounded-l-[0.625em] first:pl-5 last:rounded-r-[0.625em]',
                      'h-full w-full',
                    )}
                    style={{
                      width: `${header.column.getSize()}px`,
                    }}
                  >
                    <div
                      className={twMerge(
                        'flex w-full items-center justify-start gap-1 truncate',
                        index > 0 && '!justify-end',
                      )}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      <span className="text-sm">{flexRender(header.column.columnDef.header, header.getContext())}</span>
                      <span>
                        {
                          {
                            asc: <LiaSortUpSolid />,
                            desc: <LiaSortDownSolid />,
                          }[(header.column.getIsSorted() as string) ?? null]
                        }
                        {canHeaderSort && !header.column.getIsSorted() && <LiaSortSolid />}
                      </span>
                    </div>
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody
          className="relative grid gap-y-1"
          style={{
            height: `${rowVirtualizer.getTotalSize()}px`,
          }}
        >
          {rowVirtualizer.getVirtualItems().map((virtualRow) => {
            const row = rows[virtualRow.index] as Row<TableInfo>;
            return (
              <tr
                data-index={virtualRow.index}
                ref={(node) => rowVirtualizer.measureElement(node)}
                key={row.id}
                style={{
                  transform: `translateY(${virtualRow.start}px)`,
                }}
                className={twMerge(
                  '!absolute flex w-full items-stretch text-ph-gray-evening',
                  '!rounded-[0.625em]',
                  // the border glow adds 1px of padding in the ::before pseudo element, border transparent occupies the same space
                  'box-border border border-transparent',
                  // border is hidden when showing the border glow
                  'hover:border-0 hover:!bg-ph-white-3 hover:border-glow',
                )}
                onDoubleClick={createTableClickHandler(row)}
              >
                {row.getVisibleCells().map((cell) => {
                  return (
                    <td
                      key={cell.id}
                      className={twMerge(
                        'box-border flex items-stretch truncate px-3 py-3 font-bold text-ph-white-50 first:pl-5 hover:cursor-pointer',
                      )}
                      style={{
                        width: `${cell.column.getSize()}px`,
                      }}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>

      <LobbyStatusLoader isFetching={!isRefetching && isFetching} totalRowCount={totalDBRowCount} />

      {selectedRow && (
        <div className="bg-red-500">
          <TableInfoModal
            open={showTableInfo}
            setClose={() => setShowTableInfo(false)}
            maxPlayers={selectedRow.maxPlayers}
            currentPlayers={selectedRow.players}
            name={selectedRow.name}
            gameParams={selectedRow.gameParams}
            currentVariant={undefined}
            variants={selectedRow.variants}
          />
        </div>
      )}
    </div>
  );
};

export default LobbyTableDesktop;
